/**
 * USD → INR repatriation engine.
 *
 * Question it answers: if you liquidated your US assets at a given moment and
 * moved the money to India, how many rupees would actually land — after US
 * tax, US penalties, forfeitures, the FX spread, wire fees, and Indian tax?
 *
 * Three things decide most of the answer, and a single "marginal rate" hides
 * all of them:
 *
 *  1. Brackets. A large 401(k) withdrawal is taxed across the whole schedule,
 *     so the 2026 federal brackets are applied rather than one rate.
 *  2. Timing and US status. Selling before you move, you are a US resident.
 *     Selling after you move, a visa holder becomes a nonresident alien — no US
 *     tax on share sales, no NIIT, no state tax on retirement money — while a
 *     citizen or green-card holder stays a US taxpayer wherever they live.
 *  3. Indian residential status. RNOR: India does not tax foreign income.
 *     ROR: India taxes it, and credits US federal tax paid on the same income.
 *
 * Every simplification is listed in `ASSUMPTIONS` and shown on the page.
 */

export type FilingStatus = "single" | "mfj";
export type Timing = "before-move" | "after-move";
export type IndiaStatus = "nri" | "rnor" | "ror";
export type LandingAccount = "nre" | "nro" | "fcnr" | "rfc" | "resident";
export type TaxMethod = "brackets" | "marginal";

export interface RealEstate {
  include: boolean;
  /** Expected sale price. */
  value: number;
  mortgage: number;
  /** Purchase price plus capital improvements. */
  basis: number;
  /** Agent, closing and transfer costs as a share of the sale price. */
  sellingCostPct: number;
  /** Primary residence, meeting the 2-of-5-years test: gain exclusion applies. */
  primary: boolean;
  /** Depreciation claimed while it was a rental. Recaptured at up to 25%. */
  depreciation: number;
}

export interface RepatriationInputs {
  // ---- assets (USD, as you expect them at the time of sale)
  cash: number;
  brokerageValue: number;
  brokerageShortGain: number;
  brokerageLongGain: number;
  /** Your average USD/INR when you bought. 0 = unknown, India then uses today's rate. */
  brokerageBuyFx: number;
  k401: number;
  /** Unvested employer match included in the 401(k) balance — forfeited when you leave. */
  k401UnvestedMatch: number;
  /** Left this employer in or after the year you turned 55: no 10% penalty on this 401(k). */
  ruleOf55: boolean;
  tradIra: number;
  rothContributions: number;
  rothEarnings: number;
  /** First Roth contribution was at least five tax years ago. */
  rothFiveYears: boolean;
  hsa: number;
  /** Saved receipts for past medical expenses: withdrawable from the HSA tax-free. */
  hsaReceipts: number;
  /** RSUs expected to vest before you sell — taxed as wages when they vest. */
  rsuVesting: number;
  realEstate: RealEstate;
  realEstateBuyFx: number;

  // ---- timeline and status
  ageAtSale: number;
  /** The year the balances above are stated as of — normally now. */
  balancesAsOfYear: number;
  saleYear: number;
  /** Grow the balances from balancesAsOfYear to saleYear. */
  compound: boolean;
  /** Expected annual return on invested balances until the sale. */
  growthRate: number;
  /** Expected annual return on cash and deposits. */
  cashRate: number;
  /** Expected annual appreciation on the property. */
  propertyGrowthRate: number;
  timing: Timing;
  usPerson: boolean;
  filing: FilingStatus;
  /** Wages or other US income in the same tax year. */
  otherUsIncome: number;
  taxMethod: TaxMethod;
  /** Used only when taxMethod is "marginal". */
  federalMarginal: number;
  stateRate: number;
  indiaStatus: IndiaStatus;
  /** Other Indian taxable income in the same year, in rupees. */
  otherIndiaIncome: number;

  // ---- transfer
  fxRate: number;
  fxSpreadPct: number;
  wireFeesUsd: number;
  landing: LandingAccount;
  /** Annual interest rate assumed on the landing account, for the year-one projection. */
  landingRate: number;
}

// ------------------------------------------------------------------ tax data

/** 2026 federal brackets, IRS inflation adjustments (Rev. Proc. for tax year 2026). */
const BRACKETS: Record<FilingStatus, [number, number][]> = {
  single: [[0, 0.10], [12_400, 0.12], [50_400, 0.22], [105_700, 0.24], [201_775, 0.32], [256_225, 0.35], [640_600, 0.37]],
  mfj: [[0, 0.10], [24_800, 0.12], [100_800, 0.22], [211_400, 0.24], [403_550, 0.32], [512_450, 0.35], [768_700, 0.37]],
};
const STANDARD_DEDUCTION: Record<FilingStatus, number> = { single: 16_100, mfj: 32_200 };
/** Taxable-income thresholds where long-term gains move to 15% and 20%. */
const LTCG_THRESHOLDS: Record<FilingStatus, [number, number]> = {
  single: [49_450, 545_500],
  mfj: [98_900, 613_700],
};
const NIIT_THRESHOLD: Record<FilingStatus, number> = { single: 200_000, mfj: 250_000 };
const NIIT_RATE = 0.038;
const HOME_EXCLUSION: Record<FilingStatus, number> = { single: 250_000, mfj: 500_000 };
const RECAPTURE_CAP = 0.25;

const EARLY_PENALTY = 0.10;
const HSA_PENALTY = 0.20;

/** India, new regime, FY 2026-27: slab thresholds in rupees. */
const INDIA_SLABS: [number, number][] = [
  [0, 0], [4e5, 0.05], [8e5, 0.10], [12e5, 0.15], [16e5, 0.20], [20e5, 0.25], [24e5, 0.30],
];
const INDIA_REBATE_LIMIT = 12e5;
/** New-regime basic exemption. A resident can set any of it left unused by
 *  ordinary income against long-term gains taxed under section 112. */
const INDIA_BASIC_EXEMPTION = 4e5;
const INDIA_CESS = 0.04;
const INDIA_LTCG_RATE = 0.125;

function indiaSurcharge(totalIncome: number): number {
  if (totalIncome > 2e7) return 0.25; // new regime caps surcharge at 25%
  if (totalIncome > 1e7) return 0.15;
  if (totalIncome > 5e6) return 0.10;
  return 0;
}

/** Progressive tax on an amount, using [threshold, rate] steps. */
function progressive(amount: number, steps: [number, number][]): number {
  let tax = 0;
  for (let i = 0; i < steps.length; i++) {
    const [lo, rate] = steps[i];
    const hi = i + 1 < steps.length ? steps[i + 1][0] : Infinity;
    if (amount <= lo) break;
    tax += (Math.min(amount, hi) - lo) * rate;
  }
  return tax;
}

function topRate(amount: number, steps: [number, number][]): number {
  let r = steps[0][1];
  for (const [lo, rate] of steps) if (amount > lo) r = rate;
  return r;
}

// ------------------------------------------------------------------ defaults and limits

export const DEFAULT_REPAT: RepatriationInputs = {
  cash: 120_000,
  brokerageValue: 1_250_000,
  brokerageShortGain: 20_000,
  brokerageLongGain: 480_000,
  brokerageBuyFx: 0,
  k401: 820_000,
  k401UnvestedMatch: 0,
  ruleOf55: false,
  tradIra: 310_000,
  rothContributions: 90_000,
  rothEarnings: 55_000,
  rothFiveYears: true,
  hsa: 62_000,
  hsaReceipts: 8_000,
  rsuVesting: 0,
  realEstate: {
    include: true,
    value: 890_000,
    mortgage: 310_000,
    basis: 540_000,
    sellingCostPct: 0.06,
    primary: true,
    depreciation: 0,
  },
  realEstateBuyFx: 0,
  ageAtSale: 45,
  balancesAsOfYear: new Date().getFullYear(),
  saleYear: 2028,
  compound: true,
  growthRate: 0.06,
  cashRate: 0.035,
  propertyGrowthRate: 0.03,
  timing: "before-move",
  usPerson: false,
  filing: "mfj",
  otherUsIncome: 0,
  taxMethod: "brackets",
  federalMarginal: 0.24,
  stateRate: 0.0399,
  indiaStatus: "nri",
  otherIndiaIncome: 0,
  fxRate: 95.91,
  fxSpreadPct: 0.005,
  wireFeesUsd: 150,
  landing: "nre",
  landingRate: 0.07,
};

export interface RLimit { min: number; max: number; step: number; hint: string }
const usdLimit = (max: number, hint: string): RLimit => ({ min: 0, max, step: 1000, hint });

export const REPAT_LIMITS = {
  cash: usdLimit(50_000_000, "$0 to $50M"),
  brokerageValue: usdLimit(100_000_000, "$0 to $100M"),
  gain: usdLimit(100_000_000, "$0 up to the account value"),
  retirement: usdLimit(20_000_000, "$0 to $20M"),
  hsa: usdLimit(2_000_000, "$0 to $2M"),
  rsu: usdLimit(50_000_000, "$0 to $50M"),
  property: usdLimit(50_000_000, "$0 to $50M"),
  otherIncome: usdLimit(20_000_000, "$0 to $20M"),
  otherIndiaIncome: { min: 0, max: 1e9, step: 10_000, hint: "₹0 to ₹100 cr" },
  age: { min: 18, max: 90, step: 0.5, hint: "18 to 90, in half years" },
  saleYear: { min: 2026, max: 2060, step: 1, hint: "2026 to 2060" },
  growthRate: { min: -0.1, max: 0.3, step: 0.005, hint: "-10% to 30%" },
  rate: { min: 0, max: 0.6, step: 0.005, hint: "0% to 60%" },
  stateRate: { min: 0, max: 0.15, step: 0.0025, hint: "0% to 15%" },
  sellingCost: { min: 0, max: 0.15, step: 0.005, hint: "0% to 15%" },
  fxRate: { min: 50, max: 200, step: 0.01, hint: "₹50 to ₹200 per $" },
  buyFx: { min: 0, max: 200, step: 0.01, hint: "0 = use today's rate; else ₹30 to ₹200" },
  fxSpread: { min: 0, max: 0.05, step: 0.0005, hint: "0% to 5%" },
  wire: usdLimit(10_000, "$0 to $10,000"),
  landingRate: { min: 0, max: 0.15, step: 0.0025, hint: "0% to 15%" },
} as const;

const num = (v: unknown, lim: RLimit, fallback: number) => {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, lim.min), lim.max);
};

export interface RepatSanitized { inputs: RepatriationInputs; notices: string[] }

/** Force everything into range, and reconcile combinations that cannot coexist. */
export function sanitizeRepat(raw: Partial<RepatriationInputs>): RepatSanitized {
  const d = DEFAULT_REPAT;
  const L = REPAT_LIMITS;
  const notices: string[] = [];
  const r = { ...d, ...raw, realEstate: { ...d.realEstate, ...(raw.realEstate ?? {}) } };

  const brokerageValue = num(r.brokerageValue, L.brokerageValue, d.brokerageValue);
  let brokerageShortGain = num(r.brokerageShortGain, L.gain, 0);
  let brokerageLongGain = num(r.brokerageLongGain, L.gain, 0);
  if (brokerageShortGain + brokerageLongGain > brokerageValue) {
    const scale = brokerageValue / (brokerageShortGain + brokerageLongGain);
    brokerageShortGain *= scale;
    brokerageLongGain *= scale;
    notices.push("Unrealised gains were larger than the brokerage balance, so they were scaled down to fit.");
  }

  const k401 = num(r.k401, L.retirement, 0);
  const k401UnvestedMatch = Math.min(num(r.k401UnvestedMatch, L.retirement, 0), k401);
  const ageAtSale = num(r.ageAtSale, L.age, d.ageAtSale);
  let ruleOf55 = r.ruleOf55 === true;
  if (ruleOf55 && ageAtSale < 55) {
    ruleOf55 = false;
    notices.push("The rule of 55 only applies if you leave the employer in or after the year you turn 55, so it was switched off.");
  }

  const hsa = num(r.hsa, L.hsa, 0);
  const re = r.realEstate;
  const realEstate: RealEstate = {
    include: re.include === true,
    value: num(re.value, L.property, 0),
    mortgage: num(re.mortgage, L.property, 0),
    basis: num(re.basis, L.property, 0),
    sellingCostPct: num(re.sellingCostPct, L.sellingCost, d.realEstate.sellingCostPct),
    primary: re.primary !== false,
    depreciation: num(re.depreciation, L.property, 0),
  };
  if (realEstate.include && realEstate.mortgage > realEstate.value) {
    notices.push("The mortgage is larger than the property's value — the sale would need cash to close, which is counted as a negative.");
  }

  let filing: FilingStatus = r.filing === "single" ? "single" : "mfj";
  const timing: Timing = r.timing === "after-move" ? "after-move" : "before-move";
  const usPerson = r.usPerson === true;
  const nonresidentAlien = timing === "after-move" && !usPerson;
  if (nonresidentAlien && filing === "mfj") {
    filing = "single";
    notices.push("Nonresident aliens generally cannot file jointly, so the single schedule is used.");
  }

  let indiaStatus: IndiaStatus = r.indiaStatus === "rnor" || r.indiaStatus === "ror" ? r.indiaStatus : "nri";
  if (timing === "before-move" && indiaStatus === "ror") {
    notices.push("Selling before you move while already an Ordinary Resident of India is unusual — check the status.");
  }
  if (timing === "after-move" && indiaStatus === "nri") {
    indiaStatus = "rnor";
    notices.push("Once you have moved to India you are resident there, so the status was set to RNOR.");
  }

  const landing: LandingAccount = (["nre", "nro", "fcnr", "rfc", "resident"] as const).includes(r.landing)
    ? r.landing
    : "nre";

  return {
    inputs: {
      cash: num(r.cash, L.cash, 0),
      brokerageValue,
      brokerageShortGain,
      brokerageLongGain,
      brokerageBuyFx: num(r.brokerageBuyFx, L.buyFx, 0),
      k401,
      k401UnvestedMatch,
      ruleOf55,
      tradIra: num(r.tradIra, L.retirement, 0),
      rothContributions: num(r.rothContributions, L.retirement, 0),
      rothEarnings: num(r.rothEarnings, L.retirement, 0),
      rothFiveYears: r.rothFiveYears !== false,
      hsa,
      hsaReceipts: Math.min(num(r.hsaReceipts, L.hsa, 0), hsa),
      rsuVesting: num(r.rsuVesting, L.rsu, 0),
      realEstate,
      realEstateBuyFx: num(r.realEstateBuyFx, L.buyFx, 0),
      ageAtSale,
      balancesAsOfYear: Math.round(num(r.balancesAsOfYear, L.saleYear, d.balancesAsOfYear)),
      saleYear: Math.round(num(r.saleYear, L.saleYear, d.saleYear)),
      compound: r.compound !== false,
      growthRate: num(r.growthRate, L.growthRate, d.growthRate),
      cashRate: num(r.cashRate, L.growthRate, d.cashRate),
      propertyGrowthRate: num(r.propertyGrowthRate, L.growthRate, d.propertyGrowthRate),
      timing,
      usPerson,
      filing,
      otherUsIncome: num(r.otherUsIncome, L.otherIncome, 0),
      taxMethod: r.taxMethod === "marginal" ? "marginal" : "brackets",
      federalMarginal: num(r.federalMarginal, L.rate, d.federalMarginal),
      stateRate: num(r.stateRate, L.stateRate, d.stateRate),
      indiaStatus,
      otherIndiaIncome: num(r.otherIndiaIncome, L.otherIndiaIncome, 0),
      fxRate: num(r.fxRate, L.fxRate, d.fxRate),
      fxSpreadPct: num(r.fxSpreadPct, L.fxSpread, d.fxSpreadPct),
      wireFeesUsd: num(r.wireFeesUsd, L.wire, d.wireFeesUsd),
      landing,
      landingRate: num(r.landingRate, L.landingRate, d.landingRate),
    },
    notices,
  };
}

// ------------------------------------------------------------------ engine

export type AssetKey = "cash" | "brokerage" | "k401" | "ira" | "roth" | "hsa" | "rsu" | "property";

export interface AssetLine {
  key: AssetKey;
  label: string;
  gross: number;
  /** Forfeited match, selling costs: value that never becomes yours to move. */
  lost: number;
  usTax: number;
  penalty: number;
  netUsd: number;
  note: string;
}

export interface RepatriationResult {
  lines: AssetLine[];
  grossUsd: number;
  lostUsd: number;
  usTaxUsd: number;
  penaltyUsd: number;
  netUsd: number;
  feesUsd: number;
  /** Rupees arriving before any Indian tax. */
  grossInr: number;
  indiaTaxInr: number;
  /** Credit for US federal tax already paid on the same income. */
  indiaCreditInr: number;
  netInr: number;
  /** Everything taken off the gross, as a share of it. */
  leakage: number;
  us: {
    nonresidentAlien: boolean;
    ordinaryIncome: number;
    federalOrdinary: number;
    federalLtcg: number;
    niit: number;
    state: number;
    effectiveOnOrdinary: number;
  };
  landing: { account: LandingAccount; yearOneInterestInr: number; interestTaxInr: number; warning: string | null };
  /** How the balances were grown before anything was taxed. */
  growth: { years: number; appliedUsd: number };
  notes: string[];
}

const LABELS: Record<AssetKey, string> = {
  cash: "Checking & savings",
  brokerage: "Taxable brokerage",
  k401: "401(k)",
  ira: "Traditional IRA",
  roth: "Roth IRA / Roth 401(k)",
  hsa: "HSA",
  rsu: "RSUs vesting before you sell",
  property: "Real estate equity",
};

/** Share out a total in proportion to weights, so line items sum back exactly. */
function allocate(total: number, weights: Partial<Record<AssetKey, number>>): Partial<Record<AssetKey, number>> {
  const sum = Object.values(weights).reduce((a, b) => a + (b ?? 0), 0);
  const out: Partial<Record<AssetKey, number>> = {};
  for (const [k, w] of Object.entries(weights) as [AssetKey, number][]) {
    out[k] = sum > 0 ? (total * w) / sum : 0;
  }
  return out;
}

/**
 * Balances are entered as they stand today. This grows them to the year of the
 * sale, so the sale year is a real lever rather than a label.
 *
 * Growth is applied gross: dividends and interest earned along the way are not
 * taxed year by year here, which flatters a long wait. The mortgage is held
 * flat rather than amortised, which does the opposite.
 */
export function growToSale(p: RepatriationInputs): { inputs: RepatriationInputs; years: number } {
  const years = Math.max(0, p.saleYear - p.balancesAsOfYear);
  if (!p.compound || years === 0) return { inputs: p, years: 0 };

  const g = Math.pow(1 + p.growthRate, years);
  const c = Math.pow(1 + p.cashRate, years);
  const h = Math.pow(1 + p.propertyGrowthRate, years);

  // A bigger balance on an unchanged cost basis is a bigger unrealised gain.
  // After a year or more of waiting, that growth is long-term by the time you sell.
  const brokerageValue = p.brokerageValue * g;
  const growth = brokerageValue - p.brokerageValue;
  const longTerm = years >= 1;

  return {
    years,
    inputs: {
      ...p,
      cash: p.cash * c,
      brokerageValue,
      brokerageShortGain: p.brokerageShortGain + (longTerm ? 0 : growth),
      brokerageLongGain: p.brokerageLongGain + (longTerm ? growth : 0),
      k401: p.k401 * g,
      k401UnvestedMatch: p.k401UnvestedMatch * g,
      tradIra: p.tradIra * g,
      // Contributions are what you put in; everything the account earns is earnings.
      rothEarnings: p.rothEarnings + (p.rothContributions + p.rothEarnings) * (g - 1),
      hsa: p.hsa * g,
      realEstate: { ...p.realEstate, value: p.realEstate.value * h },
    },
  };
}

export function runRepatriation(raw: RepatriationInputs): RepatriationResult {
  const grown = growToSale(sanitizeRepat(raw).inputs);
  const p = grown.inputs;
  const nra = p.timing === "after-move" && !p.usPerson;
  const filing: FilingStatus = nra ? "single" : p.filing;
  const notes: string[] = [];

  // ---------------------------------------------------------------- the income each asset creates
  const k401Taxable = p.k401 - p.k401UnvestedMatch;
  const rothQualified = p.ageAtSale >= 59.5 && p.rothFiveYears;
  const rothTaxable = rothQualified ? 0 : p.rothEarnings;
  const hsaTaxable = Math.max(0, p.hsa - p.hsaReceipts);

  const re = p.realEstate;
  const reSellingCost = re.include ? re.value * re.sellingCostPct : 0;
  const reRealised = re.include ? re.value - reSellingCost : 0;
  const reGain = re.include ? Math.max(0, reRealised - re.basis) : 0;
  const reRecapture = Math.min(re.depreciation, reGain);
  const reCapitalGain = reGain - reRecapture;
  const reExcluded = re.include && re.primary ? Math.min(reCapitalGain, HOME_EXCLUSION[filing]) : 0;
  const reTaxableGain = reCapitalGain - reExcluded;
  const reEquity = re.include ? re.value - re.mortgage : 0;

  // Retirement money, wages and short-term gains are ordinary income.
  const ordinaryParts: Partial<Record<AssetKey, number>> = {
    k401: k401Taxable,
    ira: p.tradIra,
    roth: rothTaxable,
    hsa: hsaTaxable,
    rsu: p.rsuVesting,
    // A nonresident alien is not taxed by the US on share sales, short or long.
    brokerage: nra ? 0 : p.brokerageShortGain,
  };
  const ordinaryFromAssets = Object.values(ordinaryParts).reduce((a, b) => a + (b ?? 0), 0);
  const ordinaryIncome = p.otherUsIncome + ordinaryFromAssets;

  const ltcgParts: Partial<Record<AssetKey, number>> = {
    brokerage: nra ? 0 : p.brokerageLongGain,
    // US real property stays taxable for a nonresident: FIRPTA.
    property: reTaxableGain,
  };
  const ltcg = (ltcgParts.brokerage ?? 0) + (ltcgParts.property ?? 0);

  // ---------------------------------------------------------------- federal
  // A nonresident alien gets no standard deduction.
  const deduction = nra ? 0 : STANDARD_DEDUCTION[filing];
  const taxableOrdinary = Math.max(0, ordinaryIncome - deduction);
  const brackets = BRACKETS[filing];

  const fedOn = (x: number) =>
    p.taxMethod === "marginal" ? Math.max(0, x) * p.federalMarginal : progressive(x, brackets);

  const fedBase = fedOn(Math.max(0, p.otherUsIncome - deduction));
  const fedOrdinaryTotal = fedOn(taxableOrdinary);
  // Only the tax caused by the assets belongs to the assets.
  const fedOrdinaryFromAssets = Math.max(0, fedOrdinaryTotal - fedBase);

  // Depreciation recapture: ordinary rates, capped at 25%.
  const withRecapture = fedOn(taxableOrdinary + reRecapture) - fedOrdinaryTotal;
  const recaptureTax = Math.min(withRecapture, reRecapture * RECAPTURE_CAP);

  // Long-term gains stack on top of ordinary taxable income. Any standard
  // deduction the ordinary income did not use shelters the gains instead.
  const [t15, t20] = LTCG_THRESHOLDS[filing];
  const stackBase = taxableOrdinary + reRecapture;
  const unusedDeduction = Math.max(0, deduction - ordinaryIncome);
  const taxableLtcg = Math.max(0, ltcg - unusedDeduction);
  let federalLtcg = 0;
  if (taxableLtcg > 0) {
    if (p.taxMethod === "marginal") {
      const top = stackBase + taxableLtcg;
      federalLtcg = taxableLtcg * (top > t20 ? 0.2 : top > t15 ? 0.15 : 0);
    } else {
      const inZero = Math.max(0, Math.min(taxableLtcg, t15 - stackBase));
      const in15 = Math.max(0, Math.min(taxableLtcg - inZero, t20 - Math.max(stackBase, t15)));
      const in20 = taxableLtcg - inZero - in15;
      federalLtcg = in15 * 0.15 + in20 * 0.2;
    }
  }

  // NIIT: never for nonresident aliens; retirement distributions are not investment income.
  let niit = 0;
  const niiParts: Partial<Record<AssetKey, number>> = {
    brokerage: nra ? 0 : p.brokerageShortGain + p.brokerageLongGain,
    property: reTaxableGain + reRecapture,
  };
  if (!nra) {
    const nii = (niiParts.brokerage ?? 0) + (niiParts.property ?? 0);
    const magi = ordinaryIncome + reRecapture + ltcg;
    niit = NIIT_RATE * Math.min(nii, Math.max(0, magi - NIIT_THRESHOLD[filing]));
  }

  // ---------------------------------------------------------------- state
  // Before the move you are a state resident and everything is in scope.
  // After it, federal law bars states from taxing a nonresident's retirement
  // income, and share sales follow where you live — but the house, and RSUs
  // earned for work done in the state, remain state-source.
  const stateParts: Partial<Record<AssetKey, number>> =
    p.timing === "before-move"
      ? {
          k401: k401Taxable, ira: p.tradIra, roth: rothTaxable, hsa: hsaTaxable, rsu: p.rsuVesting,
          brokerage: p.brokerageShortGain + p.brokerageLongGain, property: reTaxableGain + reRecapture,
        }
      : { rsu: p.rsuVesting, property: reTaxableGain + reRecapture };
  const stateBase = Object.values(stateParts).reduce((a, b) => a + (b ?? 0), 0);
  const state = stateBase * p.stateRate;

  // ---------------------------------------------------------------- penalties
  const under595 = p.ageAtSale < 59.5;
  const penalties: Partial<Record<AssetKey, number>> = {
    k401: under595 && !p.ruleOf55 ? k401Taxable * EARLY_PENALTY : 0,
    ira: under595 ? p.tradIra * EARLY_PENALTY : 0,
    roth: under595 ? rothTaxable * EARLY_PENALTY : 0,
    hsa: p.ageAtSale < 65 ? hsaTaxable * HSA_PENALTY : 0,
  };

  // ---------------------------------------------------------------- share taxes out to each asset
  const fedOrdAlloc = allocate(fedOrdinaryFromAssets, ordinaryParts);
  const ltcgAlloc = allocate(federalLtcg, ltcgParts);
  const niitAlloc = allocate(niit, niiParts);
  const stateAlloc = allocate(state, stateParts);

  const gross: Record<AssetKey, number> = {
    cash: p.cash,
    brokerage: p.brokerageValue,
    k401: p.k401,
    ira: p.tradIra,
    roth: p.rothContributions + p.rothEarnings,
    hsa: p.hsa,
    rsu: p.rsuVesting,
    property: reEquity,
  };
  const lost: Record<AssetKey, number> = {
    cash: 0, brokerage: 0, k401: p.k401UnvestedMatch, ira: 0, roth: 0, hsa: 0, rsu: 0, property: reSellingCost,
  };

  const lineNotes: Record<AssetKey, string> = {
    cash: "Principal is not income. No tax to move it.",
    brokerage: nra
      ? "Sold as a nonresident alien: the US does not tax the gain (if you spend under 183 days in the US that year)."
      : "Short-term gains at ordinary rates, long-term at 0/15/20% stacked on top, plus NIIT above the threshold.",
    k401: p.ruleOf55
      ? "Rule of 55: no 10% penalty on this employer's plan. Income tax still applies."
      : under595
        ? "Whole balance is ordinary income, plus the 10% early-withdrawal penalty."
        : "Whole balance is ordinary income. No penalty after 59½.",
    ira: under595 ? "Ordinary income plus the 10% penalty. The rule of 55 does not apply to IRAs." : "Ordinary income. No penalty after 59½.",
    roth: rothQualified
      ? "Qualified: contributions and earnings come out tax-free in the US."
      : "Contributions come out tax-free; earnings are taxed" + (under595 ? " and penalised 10%." : " (the five-year rule is not met)."),
    hsa: p.ageAtSale < 65
      ? "Receipts for past medical costs come out tax-free; the rest is income plus a 20% penalty before 65."
      : "After 65: income tax on non-medical withdrawals, no penalty.",
    rsu: "Taxed as wages when they vest, then sold at roughly no further gain.",
    property: re.primary
      ? `Up to $${HOME_EXCLUSION[filing].toLocaleString("en-US")} of gain is excluded as a primary home. Selling costs and the mortgage come off first.`
      : "Gain taxed at long-term rates; depreciation claimed is recaptured at up to 25%.",
  };

  const keys: AssetKey[] = ["cash", "brokerage", "k401", "ira", "roth", "hsa", "rsu", "property"];
  const lines: AssetLine[] = keys
    .filter((k) => gross[k] !== 0)
    .map((k) => {
      const usTax =
        (fedOrdAlloc[k] ?? 0) + (ltcgAlloc[k] ?? 0) + (niitAlloc[k] ?? 0) + (stateAlloc[k] ?? 0) +
        (k === "property" ? recaptureTax : 0);
      const penalty = penalties[k] ?? 0;
      return {
        key: k,
        label: LABELS[k],
        gross: gross[k],
        lost: lost[k],
        usTax,
        penalty,
        netUsd: gross[k] - lost[k] - usTax - penalty,
        note: lineNotes[k],
      };
    });

  const grossUsd = lines.reduce((a, l) => a + l.gross, 0);
  const before = raw.realEstate ?? p.realEstate;
  const grossBeforeGrowth =
    raw.cash + raw.brokerageValue + raw.k401 + raw.tradIra + raw.rothContributions + raw.rothEarnings +
    raw.hsa + raw.rsuVesting + (before.include ? before.value - before.mortgage : 0);
  const lostUsd = lines.reduce((a, l) => a + l.lost, 0);
  const usTaxUsd = lines.reduce((a, l) => a + l.usTax, 0);
  const penaltyUsd = lines.reduce((a, l) => a + l.penalty, 0);
  const netUsd = grossUsd - lostUsd - usTaxUsd - penaltyUsd;

  // ---------------------------------------------------------------- the transfer
  // A USD account in India (FCNR, RFC) defers the conversion — and its spread —
  // until you actually convert.
  const staysInUsd = p.landing === "fcnr" || p.landing === "rfc";
  const spread = staysInUsd ? 0 : Math.max(0, netUsd) * p.fxSpreadPct;
  const feesUsd = netUsd > 0 ? spread + p.wireFeesUsd : 0;
  const grossInr = Math.max(0, netUsd - feesUsd) * p.fxRate;
  if (staysInUsd) notes.push("Held in USD in India, so the conversion spread is not charged yet — it will be when you convert.");

  // ---------------------------------------------------------------- India
  let indiaTaxInr = 0;
  let indiaCreditInr = 0;
  if (p.indiaStatus === "ror") {
    const fx = p.fxRate;
    // India does not recognise 401(k), Roth or HSA wrappers: what comes out is income.
    // It also computes gains in rupees, so a weaker rupee since purchase is taxed too.
    const inrGain = (value: number, gain: number, buyFx: number) => {
      const basis = value - gain;
      return buyFx > 0 ? Math.max(0, value * fx - basis * buyFx) : Math.max(0, gain * fx);
    };
    const ordinaryInr =
      (k401Taxable + p.tradIra + p.rothEarnings + hsaTaxable + p.rsuVesting + p.brokerageShortGain) * fx;
    const brokerageLtInr = inrGain(p.brokerageValue - p.brokerageShortGain, p.brokerageLongGain, p.brokerageBuyFx);
    const propertyInr = re.include ? inrGain(reRealised, reGain, p.realEstateBuyFx) : 0;
    const cgInr = brokerageLtInr + propertyInr;

    const total = p.otherIndiaIncome + ordinaryInr + cgInr;
    // Basic exemption not used up by ordinary income shelters gains first.
    const unusedBasic = Math.max(0, INDIA_BASIC_EXEMPTION - (p.otherIndiaIncome + ordinaryInr));
    const cgTaxable = Math.max(0, cgInr - unusedBasic);
    const sur = indiaSurcharge(total);
    const slab = (x: number) => (x <= INDIA_REBATE_LIMIT && total <= INDIA_REBATE_LIMIT ? 0 : progressive(x, INDIA_SLABS));
    const ordTax = (slab(p.otherIndiaIncome + ordinaryInr) - slab(p.otherIndiaIncome)) * (1 + sur) * (1 + INDIA_CESS);
    // Surcharge on long-term gains is capped at 15%; the 87A rebate never applies to them.
    const cgTax = cgTaxable * INDIA_LTCG_RATE * (1 + Math.min(sur, 0.15)) * (1 + INDIA_CESS);

    // Treaty credit: US federal income tax on the same income, capped at India's tax on it.
    // State tax, NIIT and the early-withdrawal penalty are not creditable under the treaty.
    const usOrdinaryCredit = (fedOrdinaryFromAssets) * fx;
    const usCgCredit = (federalLtcg + recaptureTax) * fx;
    const creditOrd = Math.min(ordTax, usOrdinaryCredit);
    const creditCg = Math.min(cgTax, usCgCredit);
    indiaCreditInr = creditOrd + creditCg;
    indiaTaxInr = Math.max(0, ordTax - creditOrd) + Math.max(0, cgTax - creditCg);
    notes.push(
      "As an Ordinary Resident, India taxes these gains and withdrawals too, then credits the US federal tax paid on the same income. State tax, NIIT and the 10% penalty are not creditable.",
    );
  } else if (p.indiaStatus === "rnor") {
    notes.push("As RNOR, India does not tax income that arises outside India — which is why this window matters.");
  }
  const netInr = Math.max(0, grossInr - indiaTaxInr);

  // ---------------------------------------------------------------- where it lands
  const landingWarnings: Record<LandingAccount, string | null> = {
    nre: p.indiaStatus !== "nri" ? "NRE accounts are for non-residents. Once you are resident in India they must be redesignated." : null,
    nro: p.indiaStatus !== "nri" ? "NRO accounts are for non-residents and must be redesignated once you are resident." : null,
    fcnr:
      p.indiaStatus === "rnor"
        ? "A new FCNR deposit can only be opened while you are a non-resident. One opened earlier can run to maturity, and its interest stays tax-free in India until RNOR ends."
        : p.indiaStatus === "ror"
          ? "A new FCNR deposit can only be opened while you are a non-resident. One opened earlier can run to maturity, but its interest is now taxable."
          : null,
    rfc: p.indiaStatus === "nri" ? "RFC accounts are for returning residents — open one once you have moved." : null,
    resident: p.indiaStatus === "nri" ? "A resident account is for after you have moved. As an NRI, use NRE or FCNR." : null,
  };
  const interestTaxable =
    p.landing === "nro" || p.landing === "resident" ||
    (p.landing === "rfc" && p.indiaStatus === "ror") ||
    // NRE interest is exempt only while you are a non-resident. FCNR interest
    // stays exempt through RNOR (s.10(15)(iv)(fa)) and becomes taxable at ROR.
    (p.landing === "nre" && p.indiaStatus !== "nri") ||
    (p.landing === "fcnr" && p.indiaStatus === "ror");
  const yearOneInterestInr = netInr * p.landingRate;
  const interestMarginal = interestTaxable
    ? topRate(p.otherIndiaIncome + yearOneInterestInr, INDIA_SLABS) *
      (1 + indiaSurcharge(p.otherIndiaIncome + yearOneInterestInr)) * (1 + INDIA_CESS)
    : 0;

  const totalOut = lostUsd + usTaxUsd + penaltyUsd + feesUsd + indiaTaxInr / p.fxRate;

  return {
    lines,
    grossUsd,
    lostUsd,
    usTaxUsd,
    penaltyUsd,
    netUsd,
    feesUsd,
    grossInr,
    indiaTaxInr,
    indiaCreditInr,
    netInr,
    leakage: grossUsd > 0 ? totalOut / grossUsd : 0,
    us: {
      nonresidentAlien: nra,
      ordinaryIncome,
      federalOrdinary: fedOrdinaryFromAssets + recaptureTax,
      federalLtcg,
      niit,
      state,
      effectiveOnOrdinary: ordinaryFromAssets > 0 ? fedOrdinaryFromAssets / ordinaryFromAssets : 0,
    },
    growth: { years: grown.years, appliedUsd: grown.years > 0 ? grossUsd - grossBeforeGrowth : 0 },
    landing: {
      account: p.landing,
      yearOneInterestInr,
      interestTaxInr: yearOneInterestInr * interestMarginal,
      warning: landingWarnings[p.landing],
    },
    notes,
  };
}

/** The same assets sold at three different moments. */
export const TIMINGS: { id: string; label: string; detail: string; timing: Timing; indiaStatus: IndiaStatus; landing: LandingAccount }[] = [
  { id: "before", label: "Before you move", detail: "US resident, India NRI", timing: "before-move", indiaStatus: "nri", landing: "nre" },
  { id: "rnor", label: "After you move, during RNOR", detail: "India does not tax foreign income", timing: "after-move", indiaStatus: "rnor", landing: "rfc" },
  { id: "ror", label: "After RNOR ends", detail: "India taxes it, with a US credit", timing: "after-move", indiaStatus: "ror", landing: "rfc" },
];

export function compareTimings(p: RepatriationInputs) {
  return TIMINGS.map((t) => {
    const result = runRepatriation({ ...p, timing: t.timing, indiaStatus: t.indiaStatus, landing: t.landing });
    return { ...t, result };
  });
}

export const ASSUMPTIONS = [
  "Balances are entered as they stand today and grown to the year of the sale at the rates you set. That growth is applied gross: dividends and interest earned along the way are not taxed year by year, which flatters a long wait, while the mortgage is held flat rather than amortised, which does the opposite.",
  "Everything is sold or withdrawn in one tax year. Spreading withdrawals across years usually lowers the bracket, and often the total.",
  "Federal tax uses 2026 brackets, standard deduction and capital-gains thresholds. State tax is a flat rate on the income the state can reach.",
  "After the move, a visa holder is treated as a nonresident alien: no US tax on share sales, no NIIT, no standard deduction; retirement withdrawals are still taxed at graduated rates, and US property stays taxable under FIRPTA. Custodians often withhold 30% on retirement withdrawals and 15% of a property's sale price up front — refundable when you file, but a cash-flow cost in the meantime.",
  "A US citizen or green-card holder stays a US taxpayer after moving; only state tax falls away, except on property and on RSUs earned while working in the state.",
  "India taxes an Ordinary Resident on the whole 401(k), IRA or HSA withdrawal and on Roth earnings, and computes gains in rupees. Long-term here means held over 24 months in India, not 12 as in the US. India's surcharge is applied without marginal relief.",
  "India uses the new regime for FY 2026-27 (unchanged by Budget 2026): slabs to 30% above ₹24 L, the ₹60,000 section 87A rebate up to ₹12 L of income (never on capital gains), surcharge of 10% above ₹50 L, 15% above ₹1 cr and 25% above ₹2 cr — capped at 15% on long-term gains — and 4% cess. Marginal relief just above the ₹12 L, ₹50 L, ₹1 cr and ₹2 cr thresholds is not applied, so tax is slightly overstated for incomes just past them.",
  "Indian gains are taxed at 12.5% without indexation. A resident selling land or a building bought before 23 July 2024 may instead choose 20% with indexation, which can be lower on an old purchase; that option is not modelled.",
  "Taxing a 401(k) or IRA only when it is withdrawn assumes Form 10-EE was filed for section 89A relief in the first year as a resident. Without it, India can tax the growth inside the account as it accrues.",
  "Periodic pension payments may be taxable only in India under the treaty; a lump-sum withdrawal generally is not covered, which is what this page models.",
  "ISO and AMT effects, PFIC, estate tax and Schedule FA reporting are outside the scope of this page.",
];
